import { Task, User } from '@graphql-nexus-types';

/**
 * Whether the user is a participant of the task — an assignee, the reporter,
 * or a member of any linked team. Mirrors the BE `TaskPolicy::isParticipant`
 * rule that (together with 'manage-task') authorizes `updateTaskStatus`.
 */
export function isTaskParticipant(
  task: Task,
  user: User | null | undefined
): boolean {
  if (!user) {
    return false;
  }

  if (task.reporter?.id === user.id) {
    return true;
  }

  return isTaskAssigneeOrTeamMember(task, user);
}

/**
 * Whether the task is the user's own — they are a direct assignee or a member
 * of a linked team. Mirrors the BE `tasks` query default scope (`TaskQuery` /
 * `Tasks::matchesAssigneeArgs` when neither `assigneeId` nor
 * `includeAllAssignees` is passed), which excludes the reporter.
 */
export function isTaskAssigneeOrTeamMember(
  task: Task,
  user: User | null | undefined
): boolean {
  if (!user) {
    return false;
  }

  if ((task.assignees ?? []).some((assignee) => assignee?.id === user.id)) {
    return true;
  }

  const userTeamIds = new Set(
    (user.teams ?? []).filter((team) => !!team).map((team) => team.id)
  );
  return (task.teams ?? []).some((team) => !!team && userTeamIds.has(team.id));
}
